import { FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/lib/hooks/hooks';
import { useParams } from 'react-router';
import { useQuery, useQueryClient } from 'react-query';

import {
	cropText,
	handleApiError,
	handleGeneralError,
	handleUnexpectedError,
} from '@/lib/functions/funcUtils';
import { PlusIcon, UserCircle } from 'lucide-react';
import { generalSolicitationDropdown } from '@/constants/templateBuilderData';
import {
	AssemblerSectionButton,
	AvailableSectionMoreButton,
	AvailableSectionNodeProps,
	SelectedSectionNodeProps,
} from './GenerateNodeProps';
import CustomThemeNodeContentRenderer from './NodeRenderer';
import { SectionPagination } from './TemplatePagination';
import { openDialog } from '@/redux/slices/pageSlice';
import {
	SelectedTemplateSkeleton,
	TemplateAssemblerSkeleton,
} from './TemplateAssemblerSkeleton';
import {
	PrerequisitesDropdown,
	SectionBuilder,
	SolicitationSectionDropdown,
	TemplateLibrary,
	TemplateSetupInterface,
	TreeInterface,
} from '@/lib/types/TemplateBuilder/templateTypes';
import {
	getAllSections,
	getTemplateById,
} from '@/lib/functions/TemplateBuilder/templateCalls';
import {
	changeNodeDesign,
	extractNames,
	fillPrerequisites,
	findNodeById,
	getNodeKey,
	hasDuplicateInSectionArray,
	solicitationAssociativeArrayConversion,
	templateTreeMapper,
	updatePrerequisiteDependencyArray,
} from '@/lib/functions/TemplateBuilder/templateUtils';
import { ErrorResponse } from '@/lib/types/apiTypes';
import {
	buildTemplate,
	setEditTemplateId,
	setLoading,
	setTemplateDndVisible,
	setTemplateEditable,
	setTemplateSetupVisible,
} from '@/redux/slices/TemplateBuilder/templateSlice';
import customToast from '@/components/CustomToast/CustomToast';
import TextButton from '@/components/ui/shared/Button/TextButton/TextButton';
import { setEditSectionData } from '@/redux/slices/TemplateBuilder/sectionSlice';
import { Input } from '@/components/ui/input';
import { Multiselect } from '@/components/ui/shared/Multiselect/Multiselect';
import SecondaryButton from '@/components/ui/shared/Button/SecondaryButton/SecondaryButton';
import PrimaryButton from '@/components/ui/shared/Button/PrimaryButton/PrimaryButton';
import { RootState } from '@/redux/store/store';
import { Badge } from '@/components/ui/badge';
import AvailableSectionsFilter from './AvailableSectionsFilter';
import {
	changeNodeAtPath,
	getNodeAtPath,
	removeNodeAtPath,
	SortableTreeWithExternalManager,
} from '@nosferatu500/react-sortable-tree';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import useOrganizationId from '@/lib/hooks/useOrganizationId';
import { DndContext } from 'react-dnd';
import LoaderPanel from '@/components/Loader/LoaderPanel/LoaderPanel';
import InfoNew from '@/components/ui/icons/new/TemplateBuilder/InfoNew';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import CustomDragLayer from './CustomDragLayer';
const TemplateDragAndDrop: FC<TemplateSetupInterface> = ({
	setValue,
	form,
	onSubmit,
}) => {
	const templateDndVisible = useAppSelector(
		(root: RootState) => root.templates.isTemplateDndVisible
	);
	const templateBuilderData = useAppSelector(
		(root: RootState) => root.templates.templateBuilder
	);
	const templateTagFilter = useAppSelector(
		(root: RootState) => root.templates.templateTagFilter
	);
	const orgIdentifier = useOrganizationId();
	const { id } = useParams(); // edit template id
	/**
	 * set edit template states
	 */
	useEffect(() => {
		if (id) {
			dispatch(setTemplateEditable(true));
			dispatch(setEditTemplateId(id));
		} else {
			// dispatch(setTemplateEditable(true));
			// dispatch(setEditTemplateId(undefined));
		}
	}, [id]);
	useEffect(() => {
		dispatch(setTemplateDndVisible(false));
		dispatch(setTemplateSetupVisible(true));
	}, []);
	/**
	 * states
	 */
	const wordCountRef = useRef<HTMLInputElement | null>(null);
	const [tags, setTags] = useState<PrerequisitesDropdown[]>([]);
	const [tagOptions, setTagOptions] = useState<PrerequisitesDropdown[]>([]);
	const [solicitationSections, setSolicitationSections] = useState<
		SolicitationSectionDropdown[]
	>([]);
	const [treeData, setTreeData2] = useState<any[]>([]);
	const [nodePath, setNodePath] = useState<number[]>([]);
	const [selectedSection, setSelectedSection] =
		useState<SectionBuilder | null>();
	const [showSection, toggleSection] = useState(false);
	const [sectionSearch, setSectionSearch] = useState<string | undefined>();
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();
	// Both trees need to share this same node type in their
	// `dndType` prop
	const externalNodeType = 'sections';
	/**
	 * check user authentication
	 */
	const auth = useAppSelector((root: RootState) => root.auth.isAuthenticated);
	const isNodeDragging = useAppSelector(
		(root: RootState) => root.templates.isNodeDragging
	);
	/**
	 * section pagination and filter setup
	 */
	const [sectionCurrentPage, setSectionCurrentPage] = useState(1);
	const [sectionHasNextPage, setSectionNextPage] = useState(false);
	const [sectionHasPrevPage, setSectionPreviousPage] = useState(false);
	const [sectionTotalPages, setSectionTotalPages] = useState(0);
	const [sectionCreatedByFilter, setSectionCreatedByFilter] = useState<
		string | undefined
	>();
	const [availableSectionsTree, setAvailableSectionsTree] = useState<
		TreeInterface[]
	>([]);
	const [sectionTagFilter, setSectionTagFilter] = useState<
		string[] | undefined
	>();
	/**
	 * fetch templateById
	 */
	const templateByIdQuery = useQuery(
		['templateById', id],
		async () => {
			const { data } = await getTemplateById(id);
			return data;
		},
		{
			cacheTime: 0,
			keepPreviousData: false,
			refetchOnWindowFocus: false,
			enabled: id !== undefined,
			onSuccess: (data: TemplateLibrary) => {
				// console.log(data);
				setValue('name', data.name);
				setValue('description', data.description);
				setValue('tags', data.tags);
				setValue('organizationIdentifier', data.organizationIdentifier);
				dispatch(setTemplateDndVisible(false));
				dispatch(setTemplateSetupVisible(true));
				const updatedTreeData = fillPrerequisites(data.sections);

				data.sections = updatedTreeData;
				buildTemplateData(data);
				setTreeData2(updatedTreeData);
				queryClient.invalidateQueries('availableSections');
			},
			onError: (error: any) => {
				if (error instanceof Error) {
					// console.log(error);
					handleGeneralError(error);
				} else if (
					typeof error === 'object' &&
					error !== null &&
					'message' in error
				) {
					const apiError = error as ErrorResponse;
					handleApiError(apiError);
				} else {
					handleUnexpectedError();
				}
			},
			onSettled: () => {
				dispatch(setLoading(false));
			},
		}
	);

	/**
	 * fetch sections
	 */
	// );
	const sectionQuery = useQuery(
		['availableSections'],
		async () => {
			const param = {
				pageSize: window.innerWidth >= 1600 ? 8 : 6,
				pageNumber: sectionCurrentPage,
				isLive: 'true',
				publishType: 'template',
				search: sectionSearch,
				tag: sectionTagFilter,
				organizationIdentifier: orgIdentifier,
				filter: sectionCreatedByFilter || 'all',
			};
			const { data } = await getAllSections(param);
			setSectionCurrentPage(data.currentPage);
			setSectionNextPage(data.hasNextPage);
			setSectionPreviousPage(data.hasPrevPage);
			setSectionTotalPages(data.totalPages);
			return data.sections;
		},
		{
			refetchOnWindowFocus: false,
			enabled: auth,
			onSuccess: (data: SectionBuilder[]) => {
				if (data) {
					// dispatch(setSectionData(data));
					// const autoC: any = [];
					const treeData = templateTreeMapper(data);

					setAvailableSectionsTree(treeData);
				}
			},
			onError: (error: any) => {
				if (error instanceof Error) {
					handleGeneralError(error);
				} else if (
					typeof error === 'object' &&
					error !== null &&
					'message' in error
				) {
					const apiError = error as ErrorResponse;
					handleApiError(apiError);
				} else {
					handleUnexpectedError();
				}
			},
			onSettled: () => {
				dispatch(setLoading(false));
			},
		}
	);
	useEffect(() => {
		sectionQuery.refetch();
	}, [
		sectionCurrentPage,
		sectionSearch,
		sectionCreatedByFilter,
		sectionTagFilter,
	]);

	/**
	 * once user click on node in available sections
	 * @param node
	 */
	const selectedNodeData = (node: any) => {
		// console.log('selectedNodData',node)
		setSelectedSection(undefined);
		if (wordCountRef.current) {
			wordCountRef.current.value = node.wordCount;
		}
		toggleSection(true);
		if (node.prerequisitesDropdown && node.prerequisitesDropdown.length > 0) {
			setTags(node.prerequisitesDropdown);
		} else {
			// console.log('testing')
			// setTags([]);
		}

		if (
			node.solicitationSectionDropdown &&
			node.solicitationSectionDropdown.length > 0
		) {
			const sol = solicitationAssociativeArrayConversion(
				node.solicitationSectionDropdown
			);
			setSolicitationSections(sol);
		} else {
			if (node.solicitation && node.solicitation.length > 0) {
				const sol = solicitationAssociativeArrayConversion(node.solicitation);
				if (Object.isExtensible(node)) {
					node.solicitationSectionDropdown = sol;
				} else {
					node = { ...node, solicitationSectionDropdown: sol };
				}
				setSolicitationSections(sol);
			} else {
				setSolicitationSections([]);
			}
		}
		setSelectedSection(node);
	};

	/**
	 * render everytime when templateBuilderData value changes
	 */

	useEffect(() => {
		if (templateBuilderData && templateBuilderData.sections) {
			/** fill prerequisites by default values if its values not selected */
			setTags([]);
			const updatedSectionsData = fillPrerequisites(
				templateBuilderData.sections
			);
			setTreeData2(updatedSectionsData);
			// set prerequistes dropdown values
			if (selectedSection) {
				const findNode = findNodeById(updatedSectionsData, selectedSection._id);
				if (findNode && findNode.prerequisitesDropdown.length > 0) {
					setTags(findNode.prerequisitesDropdown);
				} else {
					setTags([]);
				}
			}
			const depSet = new Set();
			templateBuilderData.sections.forEach((section) => {
				updatePrerequisiteDependencyArray(section, depSet);
			});
			const flattenedArray: any = Array.from(depSet);
			if (selectedSection) {
				const filteredOptions = flattenedArray.filter(
					(value) => value.label !== selectedSection.name
				);
				if (filteredOptions && filteredOptions.length > 0) {
					// console.log('filteredOptions',filteredOptions)
					setTagOptions(filteredOptions);
				} else {
					setTagOptions([]);
				}
			} else {
				setTagOptions([]);
			}
		}
		// else {
		// 	setTreeData2([]);
		// 	setTagOptions([]);
		// 	setTags([]);
		// 	// setSolicitationSectionOptions([]);
		// 	setSolicitationSections([]);
		// }
	}, [templateBuilderData, selectedSection]);

	/**
	 * dispatch updated sections values to redux template builder
	 * @param updatedTemplateBuilder
	 */
	const buildTemplateData = (updatedTemplateBuilder: any) => {
		dispatch(buildTemplate(updatedTemplateBuilder));
	};
	/**
	 * prerequisites onchange
	 * @param newTags
	 * @returns
	 */
	const updateTemplatePrerequisites = (
		newTags: [PrerequisitesDropdown, ...PrerequisitesDropdown[]]
	) => {
		const nodeExists = getNodeAtPath({
			treeData,
			path: nodePath,
			getNodeKey,
		});

		if (!nodeExists) {
			setTags([]);
			return;
		}

		if (templateBuilderData && nodeExists) {
			const updateTags = newTags.length > 0 ? newTags : [];

			const updatedData = changeNodeAtPath({
				treeData,
				path: nodePath,
				getNodeKey,
				newNode: (oldNode) => ({
					...oldNode.node,
					prerequisitesDropdown: updateTags,
				}),
			});

			setTreeData2(updatedData);

			const updatedTemplateBuilder = {
				...templateBuilderData,
				sections: updatedData,
			};

			buildTemplateData(updatedTemplateBuilder);
		}
	};
	/**
	 * solicitation sections on change method
	 */
	const updateTemplateSolicitationSections = (
		newSolicitationSection: [
			SolicitationSectionDropdown,
			...SolicitationSectionDropdown[],
		]
	) => {
		const nodeExists = getNodeAtPath({
			treeData,
			path: nodePath,
			getNodeKey,
		});

		if (!nodeExists) {
			setSolicitationSections([]);
			return;
		}

		if (templateBuilderData && nodeExists) {
			const updateSections =
				newSolicitationSection.length > 0 ? newSolicitationSection : [];

			const updatedData = changeNodeAtPath({
				treeData,
				path: nodePath,
				getNodeKey,
				newNode: (oldNode) => ({
					...oldNode.node,
					solicitationSectionDropdown: updateSections,
				}),
			});

			setTreeData2(updatedData);

			const updatedTemplateBuilder = {
				...templateBuilderData,
				sections: updatedData,
			};

			buildTemplateData(updatedTemplateBuilder);
		}
	};
	/**
	 * update wordcount and description based on selected section
	 * @param wordCount
	 * @param sectionDescription
	 * @returns
	 */
	const updateSections = (wordCount: any) => {
		const nodeExists = getNodeAtPath({
			treeData,
			path: nodePath,
			getNodeKey,
		});
		if (!nodeExists) {
			return;
		}

		if (templateBuilderData && nodeExists) {
			const updatedData = changeNodeAtPath({
				treeData:
					treeData &&
					treeData.length > 0 &&
					Object.keys(treeData[0]).length !== 0
						? treeData
						: templateBuilderData?.sections,
				path: nodePath,
				getNodeKey,
				newNode: (oldNode) => ({
					...oldNode.node,
					...(wordCount && {
						wordCount: Number(wordCount),
					}),
				}),
			});
			setTreeData2(updatedData);

			const updatedTemplateBuilder = {
				...templateBuilderData,
				sections: updatedData,
			};

			buildTemplateData(updatedTemplateBuilder);
		}
	};

	/**
	 * available sections drag
	 * @param node
	 * @returns
	 */
	const dragState = ({ node }) => {
		if (templateBuilderData && node && templateBuilderData.sections) {
			const isDuplicate = hasDuplicateInSectionArray(
				templateBuilderData.sections,
				node._id
			);
			if (isDuplicate) {
				return false;
			} else {
				return true;
			}
		}
		return true;
	};

	const TemplateTags = () => {
		const tagsFilter = templateBuilderData?.tags.every(
			(element) => typeof element === 'string'
		)
			? templateBuilderData?.tags
			: extractNames(templateBuilderData?.tags);
		return (
			<div className="flex items-center w-full gap-1">
				{tagsFilter &&
					tagsFilter.slice(0, 3).map((item) => (
						<TooltipProvider key={item}>
							<Tooltip>
								<TooltipTrigger type="button">
									<Badge
										key={item}
										className="items-center justify-center block px-3 bg-white border max-w-40 text-secondary border-secondary"
									>
										<span className="flex items-center w-full overflow-hidden font-medium uppercase text-secondary dynamic-small text-nowrap text-ellipsis">
											{cropText(item, 16)}
										</span>
									</Badge>
								</TooltipTrigger>
								<TooltipContent className="bg-white text-[#5D6F79] dynamic-small rounded-2xl font-normal">
									{item}
								</TooltipContent>
							</Tooltip>
						</TooltipProvider>
					))}
				{tagsFilter && tagsFilter.length > 3 && (
					<Popover>
						<PopoverTrigger onClick={(e) => e.stopPropagation()}>
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger
										type="button"
										className="flex items-center justify-center"
									>
										<ChevronRight className={cn('text-[#5D6F79]')} size={18} />
									</TooltipTrigger>
									<TooltipContent className="bg-white text-[#5D6F79] font-normal dynamic-small rounded-2xl">
										View More Tags
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</PopoverTrigger>
						<PopoverContent
							className="gap-2 py-4 bg-white rounded-md "
							align="end"
						>
							<h2 className="text-[#5D6F79] font-bold mb-3">Template Tags</h2>
							<div className="flex flex-wrap gap-1">
								{tagsFilter &&
									tagsFilter.map((item) => (
										<Badge
											key={item}
											className="items-center justify-center block px-3 bg-white border text-secondary border-secondary"
										>
											{item.length > 35 ? (
												<TooltipProvider>
													<Tooltip>
														<TooltipTrigger
															tabIndex={-1}
															className="flex text-left"
														>
															<span className="font-medium uppercase text-secondary dynamic-badge">
																{cropText(item, 35)}
															</span>
														</TooltipTrigger>
														<TooltipContent className="bg-white text-[#5D6F79] font-normal dynamic-small rounded-2xl">
															{item}
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											) : (
												<span className="flex items-center w-full overflow-hidden font-medium uppercase text-secondary dynamic-badge">
													{item}
												</span>
											)}
										</Badge>
									))}
							</div>
						</PopoverContent>
					</Popover>
				)}
			</div>
		);
	};
	const removeNodeFromTree = (path: number[]) => {
		/**
		 * remove node from the tree as well
		 */
		changeNodeDesign(selectedSection, true);
		toggleSection(false);
		setSelectedSection(undefined);

		const pathExists = getNodeAtPath({
			treeData,
			path,
			getNodeKey,
		});

		if (!pathExists) {
			customToast.error({
				title: 'No node found at the given path',
			});
			return;
		}
		const updatedTree = removeNodeAtPath({
			treeData,
			path,
			getNodeKey,
		});

		setTreeData2(updatedTree);

		const updatedTemplateBuilder = {
			...templateBuilderData,
			sections: updatedTree,
		};
		buildTemplateData(updatedTemplateBuilder);
		// setSectionCurrentPage(1);
		// queryClient.invalidateQueries('availableSections');
	};

	useEffect(() => {
		if (
			// sectionQuery.isLoading ||
			// sectionQuery.isFetching ||
			templateByIdQuery.isLoading ||
			templateByIdQuery.isFetching
		) {
			dispatch(setLoading(true));
		}
	}, [sectionQuery, templateByIdQuery]);

	const handleSearchChange = (value: string) => {
		setSectionSearch(value);
		setSectionCurrentPage(1);
	};
	const handleOrganizationFilter = (value: string) => {
		setSectionCreatedByFilter(value);
		setSectionCurrentPage(1);
	};
	// handle tag filter
	useEffect(() => {
		setSectionTagFilter(templateTagFilter);
		setSectionCurrentPage(1);
	}, [templateTagFilter]);
	return (
		<>
			<div
				className={`grid grid-cols-12 gap-5 w-full rounded-[16px] ${
					templateDndVisible ? 'visible' : 'hidden'
				}`}
			>
				{/* available sections */}
				<div className="section-area relative flex flex-col col-span-3 gap-3 bg-white px-4 pt-4 rounded-[16px] shadow-main">
					<div className="flex flex-col justify-between gap-1">
						<div className="flex flex-row items-center gap-2">
							<h2 className="font-bold text-black dynamic-xl-large">
								Sections
							</h2>

							<TooltipProvider delayDuration={50}>
								<Tooltip>
									<TooltipTrigger type="button">
										<InfoNew />
									</TooltipTrigger>
									<TooltipContent
										align="start"
										side="bottom"
										className="bg-white overflow-y-auto text-wrap max-w-sm break-words text-[#5D6F79] 
													dynamic-small py-4 px-4 rounded-xl border-none shadow-none drop-shadow-effect"
									>
										Here are the sections you can find and then drag and drop in
										the Template Assembler to create a template.
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</div>
						<div className="flex flex-row">
							<span className="dynamic-small text-[#5D6F79]">
								Drag and Drop these sections into the template assembler.
							</span>
						</div>
						<div className="flex flex-row items-center">
							<TextButton
								type="button"
								className="create-section"
								onClick={() => {
									dispatch(setEditSectionData(null));
									dispatch(
										openDialog({
											type: 'createTemplateSection',
											editSectionID: null,
											sectionCreatedByUser: true,
											sectionDialogStep: '1',
										})
									);
								}}
							>
								<PlusIcon size={18} /> Create NEW SECTION
							</TextButton>
						</div>
						<div className="bg-[#E9E9E9] h-[1px] border-none mb-3"></div>
						<AvailableSectionsFilter
							handleSearchChange={handleSearchChange}
							sectionSearch={sectionSearch}
							handleOrganizationFilter={handleOrganizationFilter}
						/>
					</div>
					{!sectionQuery.isFetchedAfterMount && sectionQuery.isFetching ? (
						<LoaderPanel />
					) : (
						<>
							{(sectionQuery.isFetching || sectionQuery.isLoading) && (
								<LoaderPanel />
							)}
							<div className=" h-full bg-white flex flex-col gap-5.5 2xl:gap-3 justify-between">
								<DndContext.Consumer>
									{({ dragDropManager }) =>
										!dragDropManager ? undefined : (
											<SortableTreeWithExternalManager
												dragDropManager={dragDropManager}
												treeData={availableSectionsTree}
												onChange={() => null}
												dndType={externalNodeType}
												maxDepth={0}
												canDrag={dragState}
												className="font-bold dynamic-medium available-sections"
												style={{ height: '100%' }}
												nodeContentRenderer={(nodeInfo) => (
													<CustomThemeNodeContentRenderer
														{...nodeInfo}
														{...nodeInfo.nodeProps} // Access nodeProps within the callback
														showWordCount={false}
													/>
												)}
												generateNodeProps={(rowInfo) => ({
													buttons: [
														<AvailableSectionMoreButton
															rowInfo={rowInfo}
															visible={
																rowInfo.node.organizationIdentifier
																	? true
																	: false
															}
														/>,
													],

													title: (
														<>
															<div className="dnd_tooltip">
																<AvailableSectionNodeProps rowInfo={rowInfo} />
															</div>
														</>
													),
												})}
											/>
										)
									}
								</DndContext.Consumer>
								{/* <div className="absolute left-0 bottom-12 right-0 w-full h-7 bg-gradient-to-b from-transparent to-[rgba(255,255,255,1)]" /> */}
								<SectionPagination
									setSectionCurrentPage={setSectionCurrentPage}
									sectionCurrentPage={sectionCurrentPage}
									sectionTotalPages={sectionTotalPages}
									sectionHasNextPage={sectionHasNextPage}
									sectionHasPrevPage={sectionHasPrevPage}
								/>
							</div>
						</>
					)}
				</div>

				{/* template assembler grid */}
				<div className="flex flex-col col-span-9 gap-3">
					{/* template headings */}
					<div className="flex flex-row bg-white justify-between w-full mb-3 px-4 py-6 rounded-[16px] shadow-main">
						<div className="flex flex-col">
							<TooltipProvider delayDuration={50}>
								<Tooltip>
									<TooltipTrigger type="button">
										<h2 className="w-full overflow-hidden font-bold text-black dynamic-xl-large text-nowrap text-ellipsis">
											{templateBuilderData?.name && (
												<span className="inline-block">
													<span className="hidden md:inline 2xl:hidden">
														{cropText(templateBuilderData.name, 25)}
													</span>
													<span className="md:hidden 2xl:inline">
														{templateBuilderData.name}
													</span>
												</span>
											)}
										</h2>
									</TooltipTrigger>
									<TooltipContent className="bg-white text-[#5D6F79] dynamic-small rounded-2xl font-normal visible">
										{templateBuilderData?.name}
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</div>
						<div className="flex flex-row items-end justify-end">
							<TemplateTags />
						</div>
					</div>
					{/* template assembler grid */}
					<div className="grid grid-cols-12 gap-5 ">
						{/* template assembler */}
						<div className="template-node relative flex flex-col col-span-6 gap-5 bg-white px-4 py-7 rounded-[16px] selected-sections shadow-main">
							<div className="flex flex-row items-center gap-2">
								<h2 className="font-bold text-black dynamic-xl-large">
									Template Assembler
								</h2>
								<TooltipProvider delayDuration={50}>
									<Tooltip>
										<TooltipTrigger type="button">
											<InfoNew />
										</TooltipTrigger>
										<TooltipContent
											align="start"
											side="top"
											className="bg-white overflow-y-auto text-wrap max-w-sm break-words text-[#5D6F79] 
													dynamic-small py-4 px-4 rounded-xl border-none shadow-none drop-shadow-effect"
										>
											You can drag and drop sections from the available sections
											list here to build out a template. Push sections under a
											main section to make it a subsection.
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
							<div
								className={cn(
									`relative h-[calc(100vh-250px)] 2xl:h-[calc(100vh-350px)] template-assembler px-6 z-10`,
									isNodeDragging ? 'node-dragging' : ''
								)}
							>
								<CustomDragLayer screenWidth={1600} />
								<DndContext.Consumer>
									{({ dragDropManager }) =>
										!dragDropManager ? undefined : (
											<SortableTreeWithExternalManager
												dragDropManager={dragDropManager}
												treeData={treeData}
												placeholderRenderer={TemplateAssemblerSkeleton}
												onChange={(treeD) => {
													if (templateBuilderData) {
														const updatedSections = [...treeD];

														const updatedTemplateBuilder = {
															...templateBuilderData,
															sections: updatedSections,
														};

														buildTemplateData(updatedTemplateBuilder);
													}

													setTreeData2(treeD);
												}}
												maxDepth={3}
												dndType={externalNodeType}
												className="font-bold dynamic-medium"
												style={{ height: '100%' }}
												generateNodeProps={({ node, path }) => ({
													buttons: [
														<AssemblerSectionButton
															node={node}
															path={path}
															selectedSection={selectedSection}
															removeNodeFromTree={removeNodeFromTree}
														/>,
													],
													title: <SelectedSectionNodeProps node={node} />,
												})}
												nodeContentRenderer={(nodeInfo) => (
													<CustomThemeNodeContentRenderer
														{...nodeInfo}
														{...nodeInfo.nodeProps} // Access nodeProps within the callback
														selectedNodeData={selectedNodeData}
														setNodePath={setNodePath}
														showWordCount={true}
													/>
												)}
												onMoveNode={({ node, nextPath }) => {
													// console.log('onMoveNode',node)
													selectedNodeData(node);
													setNodePath(nextPath);
													setTimeout(() => {
														changeNodeDesign(node);
													}, 100);
												}}
												onVisibilityToggle={() => {
													changeNodeDesign(selectedSection, true);
													toggleSection(false);
													setSelectedSection(undefined);
												}}
											/>
										)
									}
								</DndContext.Consumer>
								{!isNodeDragging && (
									<div className="absolute left-0 bottom-0 right-0 w-full h-[5px] 2xl:h-[10px] bg-gradient-to-b from-transparent to-[rgba(255,255,255,1)]" />
								)}
							</div>
							{treeData?.length > 0 && 3 > treeData?.length && (
								<div className="absolute bottom-0 z-0">
									<TemplateAssemblerSkeleton
										showBoundary={false}
										showFirstSection={false}
										showCompact={true}
										className={`2xl:px-[120px] px-[80px] gap-2 justify-end ${treeData?.length === 2 ? 'py-20' : 'py-32'}`}
									/>
								</div>
							)}
						</div>
						{/* selected node skeleton */}
						<div
							className={`template-setting flex flex-col col-span-6 items-center bg-white px-[150px] justify-center rounded-[16px] shadow-main ${
								showSection ? 'hidden' : 'visible'
							}`}
						>
							<SelectedTemplateSkeleton />
						</div>
						{/* selected node sections */}
						<div
							className={`flex flex-col col-span-6 gap-3 bg-white px-4 pt-7 pb-2 rounded-[16px] shadow-main ${
								showSection ? 'visible' : 'hidden'
							}`}
						>
							<div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-between gap-2 2xl:gap-5 overflow-hidden">
								<div className="flex flex-row flex-grow items-center gap-2 w-full overflow-hidden">
									{selectedSection &&
										selectedSection?.organizationIdentifier && (
											<UserCircle
												size={18}
												className="flex flex-grow min-w-4"
												color="#E861C9"
											/>
										)}{' '}
									<TooltipProvider delayDuration={50}>
										<Tooltip>
											<TooltipTrigger type="button" className="w-full">
												<h2 className="flex w-full overflow-hidden font-bold text-black dynamic-xl-large text-nowrap text-ellipsis">
													{selectedSection?.name && (
														<span className="truncate pr-2">
															{selectedSection.name}
														</span>
													)}
												</h2>
											</TooltipTrigger>
											<TooltipContent className="bg-white text-[#5D6F79] dynamic-small rounded-2xl font-normal">
												{selectedSection?.name}
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								</div>
								<div className="flex items-center gap-1">
									{selectedSection?.tags &&
										selectedSection.tags.slice(0, 2).map((item) => (
											<TooltipProvider key={item}>
												<Tooltip>
													<TooltipTrigger type="button">
														<Badge
															key={item}
															className="items-center justify-center block px-3 bg-white border max-w-40 text-secondary border-secondary"
														>
															<span className="flex items-center w-full overflow-hidden font-medium uppercase text-secondary dynamic-small text-nowrap text-ellipsis">
																{cropText(item, 16)}
															</span>
														</Badge>
													</TooltipTrigger>
													<TooltipContent className="bg-white text-[#5D6F79] dynamic-small rounded-2xl font-normal">
														{item}
													</TooltipContent>
												</Tooltip>
											</TooltipProvider>
										))}
									{selectedSection?.tags && selectedSection.tags.length > 2 && (
										<Popover>
											<PopoverTrigger onClick={(e) => e.stopPropagation()}>
												<TooltipProvider>
													<Tooltip>
														<TooltipTrigger
															type="button"
															className="flex items-center justify-center"
														>
															<ChevronRight
																className={cn('text-[#5D6F79]')}
																size={18}
															/>
														</TooltipTrigger>
														<TooltipContent className="bg-white text-[#5D6F79] font-normal dynamic-small rounded-2xl">
															View More Tags
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											</PopoverTrigger>
											<PopoverContent
												className="gap-2 py-4 bg-white rounded-md "
												align="end"
											>
												<h2 className="text-[#5D6F79] font-bold mb-3">
													Section Tags
												</h2>
												<div className="flex flex-wrap gap-1">
													{selectedSection?.tags &&
														selectedSection.tags.map((item) => (
															<Badge
																key={item}
																className="items-center justify-center block px-3 bg-white border text-secondary border-secondary"
															>
																{item.length > 35 ? (
																	<TooltipProvider>
																		<Tooltip>
																			<TooltipTrigger
																				tabIndex={-1}
																				className="flex text-left"
																			>
																				<span className="font-medium uppercase text-secondary dynamic-badge">
																					{cropText(item, 35)}
																				</span>
																			</TooltipTrigger>
																			<TooltipContent className="bg-white text-[#5D6F79] font-normal dynamic-small rounded-2xl">
																				{item}
																			</TooltipContent>
																		</Tooltip>
																	</TooltipProvider>
																) : (
																	<span className="flex items-center w-full overflow-hidden font-medium uppercase text-secondary dynamic-badge">
																		{item}
																	</span>
																)}
															</Badge>
														))}
												</div>
											</PopoverContent>
										</Popover>
									)}
								</div>
							</div>
							<div className="flex flex-row">
								<p className="text-black dynamic-text">
									{selectedSection?.description && (
										<TooltipProvider>
											<Tooltip>
												<TooltipTrigger type="button" className="flex">
													<span className="inline-block text-left text-black">
														<span className="inline md:inline 2xl:hidden">
															{cropText(selectedSection.description, 160)}
														</span>
														<span className="hidden md:hidden 2xl:inline">
															{selectedSection.description}
														</span>
													</span>
												</TooltipTrigger>
												<TooltipContent
													className="bg-white overflow-y-auto text-wrap max-w-md break-words text-[#5D6F79] 
												dynamic-small py-4 px-4 rounded-xl border-none shadow-none drop-shadow-effect"
												>
													{selectedSection.description}
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									)}
								</p>
							</div>

							<div className="bg-[#E9E9E9] h-[1px] border-none mb-5"></div>
							<div className={`flex flex-col gap-5 `}>
								<div className="flex flex-col gap-3">
									<h2 className="font-bold leading-3 text-black dynamic-large">
										Words Count
									</h2>
									<span className="text-black dynamic-small">
										Please enter the max word count for this section.
									</span>
									<Input
										type="number"
										ref={wordCountRef}
										maxLength={4}
										min={0}
										onKeyDown={(e) =>
											['e', 'E', '+', '-', 'Enter'].includes(e.key) &&
											e.preventDefault()
										}
										onInput={(event: any) => {
											if (event.target.value.length > event.target.maxLength) {
												event.target.value = event.target.value.slice(
													0,
													event.target.maxLength
												);
											}
											if (event.target.value > 1000) {
												customToast.dismiss();
												customToast.error({
													title:
														'Word count limit exceed. Can not add value greater than 1000.',
												});
												event.target.value = event.target.value.slice(0, -1);
											}
										}}
										onChange={(event) => updateSections(event.target.value)}
										placeholder="Enter word count"
										className={`p-4 rounded-xl bg-white h-12 dynamic-small
										text-black border`}
									/>
								</div>
								{tagOptions && tagOptions.length > 0 && (
									<div className="flex flex-col gap-3">
										<h2 className="font-bold leading-3 text-black dynamic-large">
											Which sections the selected section connects to
										</h2>
										<span className="text-black dynamic-small">
											Connect sections that can help inform the creation of this
											section
										</span>
										<Multiselect
											title="Select prerequisites"
											options={tagOptions}
											value={tags}
											canAddOption={false}
											onSelectedValuesChange={(values) => {
												updateTemplatePrerequisites(
													values as [
														PrerequisitesDropdown,
														...PrerequisitesDropdown[],
													]
												);
											}}
										></Multiselect>
									</div>
								)}

								{/* required solicitation sections */}
								<div className="flex flex-col gap-3">
									<h2 className="font-bold leading-3 text-black dynamic-large">
										Information required from the solicitation to write this
										section
									</h2>
									<span className="text-black dynamic-small">
										Select parts of the solicitation that will help inform this
										section.{' '}
										<b>
											You can select a maximum of two sections from the
											solicitation.
										</b>
									</span>
									<Multiselect
										title="Select Sections"
										options={generalSolicitationDropdown || []}
										value={solicitationSections}
										canAddOption={false}
										onSelectedValuesChange={(values) => {
											if (values.length > 2) {
												customToast.dismiss();
												customToast.error({
													title: 'Max 2 Options can be selected',
												});
												values.pop();
												setSolicitationSections(values.slice(0, 2));
												return;
											} else {
												updateTemplateSolicitationSections(
													values.slice(0, 2) as [
														SolicitationSectionDropdown,
														...SolicitationSectionDropdown[],
													]
												);
											}
										}}
									></Multiselect>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="grid grid-cols-8">
				<div
					className={`flex flex-row col-start-6 col-span-6 ml-5 justify-start gap-3 items-center mt-3  ${
						templateDndVisible ? 'visible' : 'hidden'
					}`}
				>
					<SecondaryButton
						type="button"
						onClick={() => {
							dispatch(
								openDialog({
									type: 'notification',
									notificationObj: {
										title: 'Go Back',
										description: 'Are you sure you want to go back ?',
										onConfirm: () => {
											changeNodeDesign(selectedSection, true);
											setSelectedSection(undefined);
											toggleSection(false);
											dispatch(setTemplateSetupVisible(true));
											dispatch(setTemplateDndVisible(false));
										},
									},
								})
							);
						}}
					>
						Back
					</SecondaryButton>
					<PrimaryButton
						type="button"
						disabled={
							treeData && Object.keys(treeData).length === 0 ? true : false
						}
						onClick={() => {
							if (treeData && Object.keys(treeData).length === 0) return;

							dispatch(
								openDialog({
									type: 'notification',
									notificationObj: {
										title: 'Save Template',
										description:
											'Are you sure you want to save this template ?',
										onConfirm: () => {
											form.handleSubmit(onSubmit)();
										},
									},
								})
							);
						}}
					>
						Save this template
					</PrimaryButton>
				</div>
			</div>
		</>
	);
};
export default TemplateDragAndDrop;
